import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'providers/reactQueryClient';
import { FULLSTORY_EVENTS } from '../../common/constants.ts';
import { trackEventFullStory } from '../../helpers/utils.ts';
import {
  SUBMISSION_MUTATION_KEYS,
  SUBMISSION_QUERY_KEY,
  UNDERWRITING_QUESTIONS_QUERY_KEY,
} from './submission.constants';
import {
  addExposure,
  addGroupField,
  bulkAddGroupField,
  bulkDeleteGroupField,
  bulkUpdateExposure,
  bulkUpdateGroupField,
  createQuoteFromSubmission,
  createSubmission,
  deleteExposure,
  deleteGroupField,
  updateExposure,
  updateGroupField,
  updatePaperlessOptIn,
  updatePaymentPlan,
  updatePolicyData,
  updateSubmission,
  updateUwQuestions,
} from './submission.requests';
import { submissionSelector } from './submission.selectors';
import type { Submission } from './submission.types';

const {
  UPDATE_UW_QUESTIONS,
  UPDATE_POLICY_DATA,
  ADD_EXPOSURE,
  UPDATE_EXPOSURE,
  BULK_UPDATE_EXPOSURE,
  DELETE_EXPOSURE,
  ADD_GROUP_FIELD,
  BULK_ADD_GROUP_FIELD,
  UPDATE_GROUP_FIELD,
  BULK_UPDATE_GROUP_FIELD,
  BULK_DELETE_GROUP_FIELD,
  DELETE_GROUP_FIELD,
  CREATE_QUOTE_FROM_SUBMISSION,
  CREATE_SUBMISSION,
  UPDATE_SUBMISSION,
  UPDATE_PAYMENT_PLAN,
  UPDATE_PAPERLESS_OPT_IN,
} = SUBMISSION_MUTATION_KEYS;

const updateSubmissionCache = (submission: Submission) => {
  queryClient.setQueryData([SUBMISSION_QUERY_KEY, submission.id], submissionSelector(submission));
};

export const useCreateSubmission = () =>
  useMutation({
    mutationKey: [CREATE_SUBMISSION],
    mutationFn: createSubmission,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

export const useUpdateSubmission = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_SUBMISSION, submissionId],
    mutationFn: updateSubmission,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

export const useCreateQuoteFromSubmission = (submissionId: string) =>
  useMutation({
    mutationKey: [CREATE_QUOTE_FROM_SUBMISSION, submissionId],
    mutationFn: createQuoteFromSubmission,
    onSuccess: (submission) => {
      trackEventFullStory(FULLSTORY_EVENTS.BEGIN_QUOTE_CREATE);
      updateSubmissionCache(submission);
    },
  });

export const useUpdatePaymentPlan = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_PAYMENT_PLAN, submissionId],
    mutationFn: updatePaymentPlan,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

export const useUpdateUwQuestions = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_UW_QUESTIONS, submissionId],
    mutationFn: updateUwQuestions,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

export const useUpdatePolicyData = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_POLICY_DATA, submissionId],
    mutationFn: updatePolicyData,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

// #region exposures
export const useAddExposure = (submissionId: string) =>
  useMutation({
    mutationKey: [ADD_EXPOSURE, submissionId],
    mutationFn: addExposure,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

export const useUpdateExposure = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_EXPOSURE, submissionId],
    mutationFn: updateExposure,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });

export const useBulkUpdateExposure = (submissionId: string, updateCache = false) =>
  useMutation({
    mutationKey: [BULK_UPDATE_EXPOSURE, submissionId],
    mutationFn: bulkUpdateExposure,
    onSuccess: (submission) => {
      if (updateCache) {
        updateSubmissionCache(submission);
      }
    },
  });

export const useDeleteExposure = (submissionId: string) =>
  useMutation({
    mutationKey: [DELETE_EXPOSURE, submissionId],
    mutationFn: deleteExposure,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
    },
  });
// #endregion

// #region group fields
export const useAddGroupField = (submissionId: string) =>
  useMutation({
    mutationKey: [ADD_GROUP_FIELD, submissionId],
    mutationFn: addGroupField,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
      queryClient.invalidateQueries({ queryKey: [UNDERWRITING_QUESTIONS_QUERY_KEY, submissionId] });
    },
  });

export const useBulkAddGroupField = (submissionId: string, updateCache = false) =>
  useMutation({
    mutationKey: [BULK_ADD_GROUP_FIELD, submissionId],
    mutationFn: bulkAddGroupField,
    onSuccess: (submission) => {
      if (updateCache) {
        updateSubmissionCache(submission);
      }
    },
  });

export const useUpdateGroupField = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_GROUP_FIELD, submissionId],
    mutationFn: updateGroupField,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
      queryClient.invalidateQueries({ queryKey: [UNDERWRITING_QUESTIONS_QUERY_KEY, submissionId] });
    },
  });

export const useBulkUpdateGroupField = (submissionId: string, updateCache = false) =>
  useMutation({
    mutationKey: [BULK_UPDATE_GROUP_FIELD, submissionId],
    mutationFn: bulkUpdateGroupField,
    onSuccess: (submission) => {
      if (updateCache) {
        updateSubmissionCache(submission);
      }
    },
  });

export const useBulkDeleteGroupField = (submissionId: string, updateCache = false) =>
  useMutation({
    mutationKey: [BULK_DELETE_GROUP_FIELD, submissionId],
    mutationFn: bulkDeleteGroupField,
    onSuccess: (submission) => {
      if (updateCache) {
        updateSubmissionCache(submission);
      }
    },
  });

export const useDeleteGroupField = (submissionId: string) =>
  useMutation({
    mutationKey: [DELETE_GROUP_FIELD, submissionId],
    mutationFn: deleteGroupField,
    onSuccess: (submission) => {
      updateSubmissionCache(submission);
      queryClient.invalidateQueries({ queryKey: [UNDERWRITING_QUESTIONS_QUERY_KEY, submissionId] });
    },
  });
// #endregion

export const useUpdatePaperlessOptIn = (submissionId: string) =>
  useMutation({
    mutationKey: [UPDATE_PAPERLESS_OPT_IN, submissionId],
    mutationFn: ({ paperlessOptIn }: { paperlessOptIn: boolean }) =>
      updatePaperlessOptIn({ submissionId, paperlessOptIn }),
  });
